import type { Car, CarDetails } from '@/entities/Car';
import { defineStore } from 'pinia';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useStateUpdate } from '@/shared/lib/pinia/useStateUpdate';
import { difference } from '@/shared/utils';

const storeKey = 'CarStore';

function defaultState(): Car {
  return {
    carValue: null,
    carValueProps: null,
    vin: '',
    make: { id: '', name: '' },
    model: { id: '', name: '' },
    modelYear: null,
    seats: { id: '', name: '' },
    cylinders: { id: '', name: '' },
    vehicleShape: { id: '', name: '' },
    plateNumber: '',
    plateType: { id: '', name: '' },
    vehicleUsage: { id: '', name: '' },
    vehicleType: { id: '', name: '', isOnline: true },
    registrationYear: null,
  };
}

export const useCarStore = defineStore(
  storeKey,
  () => {
    const state = reactive<Car>(defaultState());
    const suppressReset = ref(false);

    const makeAndModel = computed(() => `${state.make.name} - ${state.model.name}`);

    const isCarNew = computed(() => {
      const currentYear = new Date().getFullYear();
      const registrationYearNumber = Number(state.registrationYear);
      const yearsDif = Math.abs(registrationYearNumber - Number(state.modelYear));
      return !state.plateNumber && currentYear === registrationYearNumber && yearsDif <= 2;
    });

    const isCarDetailsFulfilled = computed(
      () =>
        Boolean(state.make.id
          && state.model.id
          && state.modelYear !== null
          && state.seats.id
          && state.cylinders.id),
    );

    const resetCarState = (specificFields?: Partial<Car>) => {
      Object.assign(state, specificFields ?? defaultState());
    };

    const reset = () => {
      resetCarState();
    };

    const $update = useStateUpdate<Car>(state);

    const checkForResetCarValue = (changes: CarDetails) => {
      const currentCarDetailsSet = new Set([
        state.make.id,
        state.model.id,
        state.cylinders.id,
        state.seats.id,
        state.modelYear,
      ]);
      const newCarDetailsSet = new Set([
        changes.make.id,
        changes.model.id,
        changes.cylinders.id,
        changes.seats.id,
        changes.modelYear,
      ]);

      if (difference(currentCarDetailsSet, newCarDetailsSet).size > 0) {
        resetCarState({ carValue: null });
      }
    };

    const carDetailProps = [
      () => state.make.id,
      () => state.model.id,
      () => state.cylinders.id,
      () => state.seats.id,
      () => state.modelYear,
      () => state.registrationYear,
      () => state.vehicleUsage,
    ];

    queueMicrotask(() => {
      watch(
        carDetailProps,
        () => {
          if (suppressReset.value) return;
          resetCarState({ carValue: null });
        },
        { immediate: false },
      );
    });

    async function updateSafely(next: Partial<Car>) {
      suppressReset.value = true;
      try {
        Object.assign(state, next);
      } finally {
        await nextTick();
        suppressReset.value = false;
      }
    }

    return {
      state,
      makeAndModel,
      isCarNew,
      isCarDetailsFulfilled,
      $update,
      updateSafely,
      reset,
      checkForResetCarValue,
    };
  },
  { persist: { key: storeKey } },
);
